import { ALL } from "../../consts/routeDetails";
import {
  fetchDepotPartialCodes,
  fetchDepotConfig,
  fetchLookUpTables,
  saveLookUpTables,
  resetDepot,
  lockDepot,
  unlockDepot,
  setArtificialPostcodeEdit,
  editArtificialPostcode,
  addArtificialPostcode,
  clearArtificialPostcodeLocation,
  applyArtificialPostcodeEdit,
  cancelArtificialPostcodeEdit,
  deleteArtificialPostcode,
  deleteArtificialPostcodeLocation,
  setDepotChecksum,
} from "./thunkActions";

import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  mapEditMode: false,
  locationPlanningEnabled: false,
  pickupPlanningEnabled: false,
  sessionChecksum: null,
  checksum: null,
  lockedByUserCode: null,
  lockedByUserName: null,
  lockedDatetime: null,
  lockedByYou: false,
  lockedInDifferentBrowser: false,
  artificialPostcodeEdit: null,
  artificialPostcodeEditCopy: null,
  error: null,
  partialPostcodes: null,
  loading: {
    partialPostcodes: false,
    lookupTables: false,
    locking: false,
  },
  lookupTables: null,
  artificialTables: null,
  maxSectors: 0,
  maxPostcodes: 0,
  depotInfo: null,
  postcodeView: ALL,
};

export const depotSlice = createSlice({
  name: "depot",
  initialState,
  reducers: {
    setDepotInfo: (state, action) => {
      state.depotInfo = action.payload;
    },
    setSelectedArtificialPostcode: (state, action) => {
      state.selectedArtificialPostcode =
        action.payload?.artificialPostcode || null;
    },
    setPostcodeView: (state, action) => {
      state.postcodeView = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(resetDepot.fulfilled, () => initialState)
      .addCase(setDepotChecksum, (state, action) => {
        state.checksum = action.payload;
      })
      .addCase(fetchDepotPartialCodes.pending, (state) => {
        state.loading.partialPostcodes = true;
      })
      .addCase(fetchDepotPartialCodes.rejected, (state, action) => {
        state.loading.partialPostcodes = false;
        state.error = action.payload;
      })
      .addCase(fetchDepotPartialCodes.fulfilled, (state, action) => {
        state.loading.partialPostcodes = false;
        state.partialPostcodes = action.payload;
      })
      .addCase(fetchDepotConfig.pending, (state) => {
        state.locationPlanningEnabled = false;
        state.pickupPlanningEnabled = false;
      })
      .addCase(fetchDepotConfig.rejected, (state) => {
        state.locationPlanningEnabled = false;
        state.pickupPlanningEnabled = false;
      })
      .addCase(fetchDepotConfig.fulfilled, (state, action) => {
        state.locationPlanningEnabled = action.payload.locationPlanningEnabled;
        state.pickupPlanningEnabled = action.payload.pickupPlanningEnabled;
      })
      .addCase(fetchLookUpTables.pending, (state) => {
        state.artificialPostcodeEdit = null;
        state.artificialPostcodeEditCopy = null;
        state.sessionChecksum = null;
        state.checksum = null;
        state.lockedByUserCode = null;
        state.lockedByUserName = null;
        state.lockedDatetime = null;
        state.lockedByYou = false;
        state.lockedInDifferentBrowser = false;
        state.mapEditMode = false;
        state.loading.lookupTables = true;
      })
      .addCase(fetchLookUpTables.rejected, (state, action) => {
        state.sessionChecksum = null;
        state.checksum = null;
        state.lockedByUserCode = null;
        state.lockedByUserName = null;
        state.lockedDatetime = null;
        state.lockedByYou = false;
        state.lockedInDifferentBrowser = false;
        state.mapEditMode = false;
        state.loading.lookupTables = false;
        state.error = action.payload;
      })
      .addCase(fetchLookUpTables.fulfilled, (state, action) => {
        state.loading.lookupTables = false;
        state.sessionChecksum = action.payload.sessionChecksum;
        state.checksum = action.payload.checksum;
        state.lockedByUserCode = action.payload.lockedByUserCode;
        state.lockedByUserName = action.payload.lockedByUserName;
        state.lockedDatetime = action.payload.lockedDatetime;
        state.lockedByYou = action.payload.lockedByYou;
        state.lockedInDifferentBrowser =
          action.payload.lockedInDifferentBrowser;
        state.mapEditMode = false;
        state.artificialTables = action.payload.artificialTables;
        state.lookupTables = action.payload.lookupTables;
        state.maxSectors = action.payload.maxSectors;
        state.maxPostcodes = action.payload.maxPostcodes;
      })
      .addCase(saveLookUpTables.pending, (state) => {
        state.loading.lookupTables = true;
      })
      .addCase(saveLookUpTables.rejected, (state) => {
        state.loading.lookupTables = false;
      })
      .addCase(saveLookUpTables.fulfilled, (state, action) => {
        state.loading.lookupTables = false;
        state.checksum = action.payload.checksum;
      })
      .addCase(lockDepot.pending, (state) => {
        state.loading.locking = true;
        state.mapEditMode = false;
        state.lockedByUserCode = null;
        state.lockedByUserName = null;
        state.lockedDatetime = null;
      })
      .addCase(lockDepot.rejected, (state) => {
        state.loading.locking = false;
        state.mapEditMode = false;
        state.lockedByUserCode = null;
        state.lockedByUserName = null;
        state.lockedDatetime = null;
      })
      .addCase(lockDepot.fulfilled, (state, action) => {
        state.loading.locking = false;
        state.mapEditMode = true;
        state.lockedByUserCode = action.payload.lockedByUserCode;
        state.lockedByUserName = action.payload.lockedByUserName;
        state.lockedDatetime = action.payload.lockedDatetime;
        state.lockedByYou = true;
      })
      .addCase(unlockDepot.pending, (state) => {
        state.loading.locking = true;
      })
      .addCase(unlockDepot.rejected, (state) => {
        state.loading.locking = false;
      })
      .addCase(unlockDepot.fulfilled, (state) => {
        state.loading.locking = false;
        state.mapEditMode = false;
        state.lockedByUserCode = null;
        state.lockedByUserName = null;
        state.lockedDatetime = null;
        state.lockedByYou = null;
      })
      .addCase(setArtificialPostcodeEdit.fulfilled, (state, action) => {
        state.artificialPostcodeEdit = action.payload.artificialPostcodeEdit;
        state.artificialPostcodeEditCopy = {
          ...action.payload.artificialPostcodeEdit,
        };
      })
      .addCase(editArtificialPostcode.fulfilled, (state, action) => {
        state.artificialPostcodeEdit = action.payload.artificialPostcodeEdit;
        state.lookupTables = action.payload.lookupTables;
        state.artificialTables = action.payload.artificialTables;
      })
      .addCase(addArtificialPostcode.fulfilled, (state, action) => {
        state.selectedArtificialPostcode =
          action.payload.selectedArtificialPostcode;
        state.lookupTables = action.payload.lookupTables;
        state.artificialTables = action.payload.artificialTables;
      })
      .addCase(clearArtificialPostcodeLocation.fulfilled, (state, action) => {
        state.artificialPostcodeEdit = action.payload.artificialPostcodeEdit;
        state.lookupTables = action.payload.lookupTables;
        state.artificialTables = action.payload.artificialTables;
      })
      .addCase(applyArtificialPostcodeEdit.fulfilled, (state) => {
        state.artificialPostcodeEdit = null;
        state.artificialPostcodeEditCopy = null;
      })
      .addCase(cancelArtificialPostcodeEdit.fulfilled, (state, action) => {
        state.lookupTables = action.payload.lookupTables;
        state.artificialTables = action.payload.artificialTables;
        state.artificialPostcodeEdit = null;
        state.artificialPostcodeEditCopy = null;
      })
      .addCase(deleteArtificialPostcode.pending, (state) => {
        state.loading.lookupTables = true;
      })
      .addCase(deleteArtificialPostcode.rejected, (state) => {
        state.loading.lookupTables = false;
      })
      .addCase(deleteArtificialPostcode.fulfilled, (state, action) => {
        state.loading.lookupTables = false;
        state.lookupTables = action.payload.lookupTables;
        state.artificialTables = action.payload.artificialTables;
        state.artificialPostcodeEdit = null;
        state.artificialPostcodeEditCopy = null;
      })
      .addCase(deleteArtificialPostcodeLocation.fulfilled, (state, action) => {
        state.lookupTables = action.payload.lookupTables;
        state.artificialTables = action.payload.artificialTables;
        state.artificialPostcodeEdit = action.payload.artificialPostcodeEdit;
      });
  },
});

const { reducer, actions } = depotSlice;
const { setDepotInfo, setSelectedArtificialPostcode, setPostcodeView } =
  actions;
export {
  initialState,
  reducer,
  setDepotInfo,
  setSelectedArtificialPostcode,
  setPostcodeView,
};
